import React, { useState, useEffect } from 'react';
import { useTranslation, withTranslation, WithTranslation } from 'react-i18next';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

import { IDataChart } from '@ts/ui-models';
import MainCard from './MainCard';
import { Button, Grid, Typography } from '@ts/storybook';
import { useTheme, Theme } from '@mui/material';
import { Stack, Box, Skeleton } from '@mui/material';
import _ from 'lodash';

interface AreaChartProps extends WithTranslation {
  id: string | number;
  color?: string;
  title?: string;
  slot?: string;
  dataChart?: IDataChart;
  loading: boolean;
  onChangeSlot?: (v: string) => void;
  isMock?: boolean;
}

//Chart options
const areaChartOptions: ApexOptions = {
  chart: {
    height: '100%',
    type: 'line',
    toolbar: {
      show: false,
    },
    dropShadow: {
      enabled: true,
      top: 3,
      blur: 3,
      opacity: 1,
    },
    animations: {
      enabled: true,
      //easing: 'easeinout',
      speed: 800,
      animateGradually: {
        enabled: true,
        delay: 1500,
      },
      dynamicAnimation: {
        enabled: true,
        speed: 700,
      },
    },
  },
  dataLabels: {
    enabled: true,
  },
  stroke: {
    curve: 'smooth',
    width: 2,
  },
  grid: {
    strokeDashArray: 0,
  },
  tooltip: {
    theme: 'light',
  },
};

const mockActivityData = (slot: string | undefined): IDataChart => {
  return {
    xaxis: {
      categories:
        slot === 'month'
          ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
          : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    },
    series: [
      {
        name: 'Tasks accomplished',
        data: slot === 'month' ? [76, 85, 101, 98, 87, 105, 91, 114, 94, 86, 115, 35] : [31, 40, 28, 51, 42, 109, 100],
      },
      {
        name: 'Containers delivered',
        data: slot === 'month' ? [110, 60, 150, 35, 60, 36, 26, 45, 65, 52, 53, 41] : [11, 32, 45, 32, 34, 52, 41],
      },
      {
        name: 'Workers in production zones',
        data: slot === 'month' ? [110, 60, 150, 134, 136, 127, 184, 135, 19, 23, 53, 41] : [134, 136, 127, 184, 135, 19, 23],
      },
    ],
  };
};

/* const useStyles = makeStyles((theme: Theme) => ({
  positionRotatedText: { position: 'absolute', zIndex: 1, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
  rotatedText: {
    color: theme.palette.text.disabled,
    textAlign: 'center',
    transform: 'rotate(-45deg)', // Elige el ángulo de rotación deseado (en grados)
  },
  noData: {
    backgroundColor: '#c4bfbf29',
  },
})); */

const AreaChart: React.FC<AreaChartProps> = props => {
  const { t } = useTranslation();
  //#region Styles
  const theme: Theme = useTheme();

  const { secondary } = theme.palette.text;

  const line = theme.palette.divider;
  //#endregion

  const [loading, setLoading] = useState(false);
  const [series, setSeries] = useState(props.isMock ? mockActivityData(props.slot).series : props.dataChart?.series || []);
  const [options, setOptions] = useState<ApexOptions>(areaChartOptions);
  const [slot, setSlot] = useState('week');

  //#region Handlers
  const onClickSlot = (type: string): any => {
    if (type !== slot && !loading) {
      setLoading(true);
      setSlot(type);
      props.onChangeSlot && props.onChangeSlot(type);
    }
  };
  //#endregion

  //#region Functions
  const fillOptionsChart = (prevState: any) => {
    return {
      ...prevState,
      //colors: generateColorShades('#201b50', '#c5c1f7', series.length),
      xaxis: {
        categories: props.isMock ? mockActivityData(props.slot).xaxis?.categories : props.dataChart?.xaxis?.categories,
        labels: {
          style: {
            colors: [secondary],
          },
        },
        axisBorder: {
          show: true,
          color: line,
        },
        tickAmount: props.slot === 'month' ? 11 : 7,
      },
      yaxis: {
        labels: {
          style: {
            colors: [secondary],
          },
        },
      },
      grid: {
        borderColor: line,
      },
    };
  };
  const getData = () => {
    //Check if series is a list of objects
    if (Array.isArray(series) && typeof series[0] === 'number') {
      return series;
    } else if (Array.isArray(series) && typeof series[0] === 'object') {
      return series[0].data;
    }
  };
  const checkEmptyData = () => {
    if (props.isMock) return false;
    return !getData() || getData()?.length === 0;
  };
  //#endregion

  useEffect(() => {
    setOptions(prevState => fillOptionsChart(prevState));
    setSeries(props.isMock ? mockActivityData(props.slot).series : props.dataChart?.series || []);
    setLoading(false);
  }, [props.dataChart]);

  if (props.loading)
    return (
      <MainCard contentSX={{ p: 2.25 }} sx={{ mt: 2, height: '90%' }}>
        <Stack spacing={0.5}>
          <Grid container alignItems="center">
            <Grid size={{ xs: 7 }}>
              <Skeleton variant="rounded" height={32} />
            </Grid>
            <Grid size={{ xs: 1 }} />
            <Grid size={{ xs: 4 }}>
              <Skeleton variant="rounded" height={32} />
            </Grid>
          </Grid>
        </Stack>
        <Box sx={{ pt: 2.75 }}>
          <Skeleton variant="rounded" height={420} />
        </Box>
      </MainCard>
    );

  return (
    <MainCard contentSX={{ p: 2.25 }}>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid>{props.title && <Typography variant="h5" fontWeight="500" color="primary" text={props.title} />}</Grid>
        <Grid>
          <Stack direction="row" alignItems="center" spacing={0}>
            <Button
              size="small"
              onClick={() => onClickSlot('month')}
              loading={slot === 'month' && loading}
              variant={slot === 'month' ? 'outlined' : 'text'}
              label="Month"
            />
            <Button
              size="small"
              onClick={() => onClickSlot('week')}
              loading={slot === 'week' && loading}
              variant={slot === 'week' ? 'outlined' : 'text'}
              label="Week"
            />
          </Stack>
        </Grid>
      </Grid>

      <Box sx={{ height: '100%' }}>
        {/* {checkEmptyData() ? (
            <div className={classesStyle.positionRotatedText}>
              <Typography variant="h3" className={classesStyle.rotatedText} text='NO DATA' />
            </div>
          ) : null} */}
        <ReactApexChart
          options={options}
          series={series}
          // @ts-ignore
          type={areaChartOptions.chart?.type}
          height={areaChartOptions.chart?.height}
        />
      </Box>
    </MainCard>
  );
};

/* AreaChart.propTypes = {
  slot: PropTypes.string,
}; */

export default withTranslation()(AreaChart);
